import { CheckCircle2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';

interface StrategyCard {
	key: string;
	title: string;
	description: string;
}

const CONNS_BY_SUB: Record<string, string[]> = {
	Mail: [
		'SendGrid — Transactional',
		'SendGrid — Marketing',
		'Mailgun — Backup',
		'AWS SES — Emergency',
	],
	Push: ['FCM — Android Push', 'APNs — iOS Push', 'OneSignal — Cross-platform'],
	SMS: ['Twilio — Primary SMS', 'Infobip — SMS Fallback', 'Semaphore — PH SMS'],
};

interface RoutingSetupDialogProps {
	card: StrategyCard;
	open: boolean;
	onClose: () => void;
}

export function RoutingSetupDialog({
	card,
	open,
	onClose,
}: RoutingSetupDialogProps) {
	const [sub, setSub] = useState('Mail');
	const [saved, setSaved] = useState(false);
	const conns = CONNS_BY_SUB[sub] || [];

	if (saved) {
		return (
			<Dialog open={open} onOpenChange={onClose}>
				<DialogContent className="sm:max-w-md">
					<div className="flex flex-col items-center gap-4 py-4">
						<CheckCircle2 className="h-8 w-8 text-green-500" />
						<p className="text-sm">
							✓ Routing configuration saved and activated.
						</p>
					</div>
					<div className="flex justify-end">
						<Button onClick={onClose}>Done</Button>
					</div>
				</DialogContent>
			</Dialog>
		);
	}

	return (
		<Dialog open={open} onOpenChange={onClose}>
			<DialogContent className="sm:max-w-xl">
				<DialogHeader>
					<DialogTitle>{card.title}</DialogTitle>
				</DialogHeader>

				<div className="space-y-4">
					<div>
						<Label>
							Subdomain <span className="text-red-500">*</span>
						</Label>
						<Select value={sub} onValueChange={setSub}>
							<SelectTrigger className="mt-1">
								<SelectValue placeholder="Select Subdomain" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="Mail">Email</SelectItem>
								<SelectItem value="Push">Push</SelectItem>
								<SelectItem value="SMS">SMS</SelectItem>
							</SelectContent>
						</Select>
					</div>

					<div>
						<Label>
							Configuration name <span className="text-red-500">*</span>
						</Label>
						<Input placeholder="e.g. Email Fallback Chain" className="mt-1" />
					</div>

					{card.key === 'fallback' && (
						<div>
							<Label>
								Connections (ordered) <span className="text-red-500">*</span>
							</Label>
							<div className="mt-1 space-y-1.5">
								{conns.map((c, i) => (
									<div
										key={i}
										className="flex items-center gap-2 rounded border p-2 text-sm"
									>
										<span className="text-muted-foreground">⠿</span>
										<span className="w-5 text-xs text-muted-foreground">
											{i + 1}
										</span>
										<span>{c}</span>
										<span className="ml-auto text-xs text-muted-foreground">
											{i === 0 ? 'Primary' : `Fallback ${i}`}
										</span>
									</div>
								))}
							</div>
						</div>
					)}

					{card.key === 'weighted' && (
						<div>
							<Label>
								Connection weights <span className="text-red-500">*</span>
							</Label>
							<div className="mt-1 space-y-2">
								{conns.slice(0, 2).map((c, i) => (
									<div key={i}>
										<Label className="text-xs text-muted-foreground">{c}</Label>
										<div className="flex items-center gap-2">
											<Input placeholder="60" className="mt-1 w-24" />
											<span className="text-sm">%</span>
										</div>
									</div>
								))}
								<p className="text-xs text-green-600">Total: 100% ✓</p>
							</div>
						</div>
					)}

					{card.key === 'volcap' && (
						<div className="space-y-3">
							<div>
								<Label>Primary connection</Label>
								<Select>
									<SelectTrigger className="mt-1">
										<SelectValue placeholder="Select connection" />
									</SelectTrigger>
									<SelectContent>
										{conns.map((c) => (
											<SelectItem key={c} value={c}>
												{c}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<div>
								<Label>Daily cap (messages)</Label>
								<Input placeholder="10000" type="number" className="mt-1" />
							</div>
							<div>
								<Label>Overflow connection</Label>
								<Select>
									<SelectTrigger className="mt-1">
										<SelectValue placeholder="Select connection" />
									</SelectTrigger>
									<SelectContent>
										{conns.map((c) => (
											<SelectItem key={c} value={c}>
												{c}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<p className="text-xs text-muted-foreground">
								Resets UTC midnight · Alert at 80% and 100% · V0: 1 primary + 1
								overflow only
							</p>
						</div>
					)}
				</div>

				<div className="flex justify-end gap-2">
					<Button variant="outline" onClick={onClose}>
						Cancel
					</Button>
					<Button
						onClick={() => {
							setSaved(true);
							setTimeout(onClose, 1200);
						}}
					>
						✓ Configure Rule
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
}
